import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";
import { categories, regions, yearsOptions, sortOptions } from "@/mocks/categories";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 8;
const ALL_CATEGORY = {
  slug: "all",
  name: "全部电影",
  icon: "ri-movie-2-line",
  description: "浏览电影天堂收录的全部影片，按地区、年代与排序自由筛选。",
};

export default function Category() {
  const { type = "" } = useParams();
  const category = type === "all" ? ALL_CATEGORY : categories.find((c) => c.slug === type);

  const [region, setRegion] = useState("all");
  const [yearRange, setYearRange] = useState("all");
  const [sort, setSort] = useState("time");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setRegion("all");
    setYearRange("all");
    setSort("time");
    setPage(1);
  }, [type]);

  usePageMeta({
    title: category ? `${category.name} - 电影天堂电影分类` : "分类未找到 - 电影天堂",
    description: category
      ? `${category.description} 电影天堂为你精选${category.name}相关影片，支持按地区、年代与排序筛选，快速发现值得观看的作品。`
      : "未找到对应的电影分类，请返回电影天堂首页重新选择。",
    keywords: category
      ? `${category.name}电影,${category.name}片单,电影天堂,电影资源`
      : "电影天堂,电影分类",
  });

  const filtered = useMemo(() => {
    if (!category) return [];
    let list = movies.filter((m) =>
      type === "all" ? true : m.types.includes(category.name)
    );
    if (region !== "all") list = list.filter((m) => m.region === region);
    if (yearRange !== "all") {
      const minYear = 2026 - Number(yearRange);
      list = list.filter((m) => m.year >= minYear);
    }
    const sorted = [...list];
    if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    return sorted;
  }, [category, type, region, yearRange, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [region, yearRange, sort]);

  const resetFilters = () => {
    setRegion("all");
    setYearRange("all");
    setSort("time");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这个分类"
          description="该分类可能不存在或已调整，你可以返回首页重新选择感兴趣的影片分类。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      {/* 面包屑 */}
      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>电影分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{category.name}</span>
      </nav>

      <div className="mb-6 flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${category.icon} text-2xl`}></i>
        </span>
        <div>
          <h1 className="font-heading text-2xl font-black text-foreground-950">{category.name}</h1>
          <p className="mt-1 text-sm text-foreground-600">{category.description}</p>
        </div>
      </div>

      {/* 类型切换 */}
      <div className="mb-4 flex flex-wrap gap-2">
        <Link
          to="/category/all"
          className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
            type === "all"
              ? "bg-primary-500 text-background-50 dark:text-foreground-950"
              : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-400"
          }`}
        >
          全部
        </Link>
        {categories.map((c) => (
          <Link
            key={c.slug}
            to={`/category/${c.slug}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              c.slug === type
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-400"
            }`}
          >
            {c.name}
          </Link>
        ))}
      </div>

      {/* 筛选栏 */}
      <div className="rounded-lg border border-background-200 bg-background-50">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-foreground-900 md:hidden"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line"></i>
            筛选条件
          </span>
          <i className={`ri-arrow-down-s-line transition-transform ${filtersOpen ? "rotate-180" : ""}`}></i>
        </button>

        <div className={`${filtersOpen ? "block" : "hidden"} px-4 py-3 md:block md:px-4 md:py-4`}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <span className="w-14 shrink-0 text-sm text-foreground-500">地区</span>
              <div className="flex flex-wrap gap-2">
                {[{ label: "全部", value: "all" }, ...regions.map((r) => ({ label: r, value: r }))].map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRegion(r.value)}
                    className={`rounded-md px-3 py-1 text-sm transition-colors ${
                      region === r.value
                        ? "bg-accent-100 font-medium text-accent-800"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <span className="w-14 shrink-0 text-sm text-foreground-500">年代</span>
              <div className="flex flex-wrap gap-2">
                {yearsOptions.map((y) => (
                  <button
                    key={y.value}
                    type="button"
                    onClick={() => setYearRange(y.value)}
                    className={`rounded-md px-3 py-1 text-sm transition-colors ${
                      yearRange === y.value
                        ? "bg-accent-100 font-medium text-accent-800"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {y.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <span className="w-14 shrink-0 text-sm text-foreground-500">排序</span>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSort(s.value)}
                    className={`rounded-md px-3 py-1 text-sm transition-colors ${
                      sort === s.value
                        ? "bg-accent-100 font-medium text-accent-800"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-600">
        共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 部影片
      </p>

      {/* 结果 */}
      <div className="mt-4">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-film-line"
            title="没有符合条件的影片"
            description="试着放宽筛选条件，或重置后查看该分类下的全部影片。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {pageItems.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-10">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}